import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/App';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Sparkles, Users, DollarSign, MessageSquare, TrendingUp, LogOut, Mail, CheckCircle, Clock, Shield } from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';

const AdminDashboard = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [messages, setMessages] = useState([]);
  const [revenue, setRevenue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchAdminData();
  }, []);

  const fetchAdminData = async () => {
    try {
      const [statsRes, customersRes, messagesRes, revenueRes] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/admin/customers`),
        axios.get(`${API}/admin/messages`),
        axios.get(`${API}/admin/revenue`)
      ]);
      
      setStats(statsRes.data);
      setCustomers(customersRes.data);
      setMessages(messagesRes.data);
      setRevenue(revenueRes.data);
    } catch (error) {
      toast.error('Failed to load admin data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const markMessageRead = async (messageId) => {
    try {
      await axios.patch(`${API}/admin/messages/${messageId}`);
      setMessages(messages.map(m => m.id === messageId ? { ...m, status: 'read' } : m));
      toast.success('Message marked as read');
    } catch (error) {
      toast.error('Failed to update message');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
    toast.success('Logged out successfully');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl font-heading">Loading admin dashboard...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Navigation */}
      <nav className="bg-slate-900 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-heading font-bold text-white">Admin Dashboard</span>
            </div>
            
            <div className="flex items-center gap-4">
              <span className="text-slate-400 text-sm">Site Owner Portal</span>
              <Button 
                data-testid="admin-logout-button"
                onClick={handleLogout}
                variant="ghost"
                className="text-slate-400 hover:text-white"
              >
                <LogOut className="w-5 h-5 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tabs */}
        <div className="flex gap-4 mb-8 overflow-x-auto">
          <Button
            onClick={() => setActiveTab('overview')}
            className={`rounded-full font-heading ${activeTab === 'overview' ? 'bg-violet-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-100'}`}
          >
            Overview
          </Button>
          <Button
            onClick={() => setActiveTab('customers')}
            className={`rounded-full font-heading ${activeTab === 'customers' ? 'bg-violet-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-100'}`}
          >
            Customers ({customers.length})
          </Button>
          <Button
            onClick={() => setActiveTab('messages')}
            className={`rounded-full font-heading ${activeTab === 'messages' ? 'bg-violet-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-100'}`}
          >
            Messages ({messages.filter(m => m.status === 'unread').length})
          </Button>
          <Button
            onClick={() => setActiveTab('revenue')}
            className={`rounded-full font-heading ${activeTab === 'revenue' ? 'bg-violet-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-100'}`}
          >
            Revenue
          </Button>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div>
            <h2 className="text-3xl font-heading font-bold mb-6">Platform Overview</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <div className="bg-white rounded-3xl p-6 border-2 border-slate-100">
                <div className="flex items-center justify-between mb-4">
                  <Users className="w-8 h-8 text-violet-600" />
                  <span className="text-sm text-slate-600">Total Users</span>
                </div>
                <div className="text-3xl font-heading font-bold text-slate-900">{stats.total_users}</div>
              </div>

              <div className="bg-white rounded-3xl p-6 border-2 border-slate-100">
                <div className="flex items-center justify-between mb-4">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                  <span className="text-sm text-slate-600">Active Subs</span>
                </div>
                <div className="text-3xl font-heading font-bold text-slate-900">{stats.active_subscriptions}</div>
              </div>

              <div className="bg-white rounded-3xl p-6 border-2 border-slate-100">
                <div className="flex items-center justify-between mb-4">
                  <Clock className="w-8 h-8 text-blue-600" />
                  <span className="text-sm text-slate-600">Trial Users</span>
                </div>
                <div className="text-3xl font-heading font-bold text-slate-900">{stats.trial_users}</div>
              </div>

              <div className="bg-white rounded-3xl p-6 border-2 border-slate-100">
                <div className="flex items-center justify-between mb-4">
                  <MessageSquare className="w-8 h-8 text-orange-600" />
                  <span className="text-sm text-slate-600">Unread Msgs</span>
                </div>
                <div className="text-3xl font-heading font-bold text-slate-900">{stats.unread_messages}</div>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-gradient-to-br from-violet-500 to-fuchsia-500 rounded-3xl p-8 text-white">
                <h3 className="text-xl font-heading font-bold mb-4">Monthly Recurring Revenue</h3>
                <div className="text-5xl font-heading font-bold mb-2">${stats.monthly_recurring_revenue.toFixed(2)}</div>
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5" />
                  <span>From {stats.active_subscriptions} active subscriptions</span>
                </div>
              </div>

              <div className="bg-white rounded-3xl p-8 border-2 border-slate-100">
                <h3 className="text-xl font-heading font-bold mb-4">Dispute Activity</h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600">Total Disputes</span>
                    <span className="text-2xl font-heading font-bold">{stats.total_disputes}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600">Pending Review</span>
                    <span className="text-2xl font-heading font-bold text-yellow-600">{stats.pending_disputes}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Customers Tab */}
        {activeTab === 'customers' && (
          <div>
            <h2 className="text-3xl font-heading font-bold mb-6">Customer List</h2>
            <div className="bg-white rounded-3xl border-2 border-slate-100 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="text-left px-6 py-4 font-heading font-semibold text-slate-900">Name</th>
                      <th className="text-left px-6 py-4 font-heading font-semibold text-slate-900">Email</th>
                      <th className="text-left px-6 py-4 font-heading font-semibold text-slate-900">Status</th>
                      <th className="text-left px-6 py-4 font-heading font-semibold text-slate-900">Disputes</th>
                      <th className="text-left px-6 py-4 font-heading font-semibold text-slate-900">Joined</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customers.map((customer) => (
                      <tr key={customer.id} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="px-6 py-4">{customer.full_name}</td>
                        <td className="px-6 py-4 text-slate-600">{customer.email}</td>
                        <td className="px-6 py-4">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                            customer.subscription_status === 'active' ? 'bg-green-100 text-green-800' :
                            customer.subscription_status === 'trial' ? 'bg-blue-100 text-blue-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {customer.subscription_status}
                          </span>
                        </td>
                        <td className="px-6 py-4 font-medium">{customer.total_disputes}</td>
                        <td className="px-6 py-4 text-slate-600">
                          {new Date(customer.created_at).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Messages Tab */}
        {activeTab === 'messages' && (
          <div>
            <h2 className="text-3xl font-heading font-bold mb-6">Customer Messages & Alerts</h2>
            {messages.length === 0 ? (
              <div className="bg-white rounded-3xl p-12 text-center border-2 border-dashed border-slate-200">
                <MessageSquare className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-xl font-heading font-semibold mb-2">No messages yet</h3>
                <p className="text-slate-600">Customer messages will appear here</p>
              </div>
            ) : (
              <div className="space-y-4">
                {messages.map((message) => (
                  <div 
                    key={message.id}
                    className={`bg-white rounded-3xl p-6 border-2 cursor-pointer transition-all hover:shadow-lg ${
                      message.status === 'unread' ? 'border-violet-200 bg-violet-50' : 'border-slate-100'
                    }`}
                    onClick={() => setSelectedMessage(message)}
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h3 className="text-lg font-heading font-semibold">{message.subject}</h3>
                        <p className="text-sm text-slate-600">
                          From: {message.user_name} ({message.user_email})
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        {message.status === 'unread' && (
                          <span className="px-3 py-1 rounded-full text-xs font-medium bg-violet-100 text-violet-800">
                            New
                          </span>
                        )}
                        <span className="text-sm text-slate-500">
                          {new Date(message.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                    <p className="text-slate-700 line-clamp-2">{message.message}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Revenue Tab */}
        {activeTab === 'revenue' && (
          <div>
            <h2 className="text-3xl font-heading font-bold mb-6">Revenue Tracking</h2>
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div className="bg-white rounded-3xl p-8 border-2 border-slate-100">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-slate-600">Weekly Revenue</span>
                  <DollarSign className="w-6 h-6 text-green-600" />
                </div>
                <div className="text-4xl font-heading font-bold text-green-600 mb-2">
                  ${revenue?.weekly.toFixed(2)}
                </div>
                <p className="text-sm text-slate-600">Last 7 days</p>
              </div>

              <div className="bg-white rounded-3xl p-8 border-2 border-slate-100">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-slate-600">Monthly Revenue</span>
                  <DollarSign className="w-6 h-6 text-blue-600" />
                </div>
                <div className="text-4xl font-heading font-bold text-blue-600 mb-2">
                  ${revenue?.monthly.toFixed(2)}
                </div>
                <p className="text-sm text-slate-600">Last 30 days</p>
              </div>

              <div className="bg-gradient-to-br from-violet-500 to-fuchsia-500 rounded-3xl p-8 text-white">
                <div className="flex items-center justify-between mb-4">
                  <span>Yearly Projection</span>
                  <TrendingUp className="w-6 h-6" />
                </div>
                <div className="text-4xl font-heading font-bold mb-2">
                  ${revenue?.yearly.toFixed(2)}
                </div>
                <p className="text-sm text-violet-100">Based on active subs</p>
              </div>
            </div>

            <div className="bg-white rounded-3xl p-8 border-2 border-slate-100">
              <h3 className="text-2xl font-heading font-bold mb-6">Revenue Breakdown</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center py-4 border-b border-slate-100">
                  <span className="text-slate-700">Active Subscriptions</span>
                  <span className="font-heading font-bold text-xl">{revenue?.active_subscriptions}</span>
                </div>
                <div className="flex justify-between items-center py-4 border-b border-slate-100">
                  <span className="text-slate-700">Subscription Price</span>
                  <span className="font-heading font-bold text-xl">$59.99/mo</span>
                </div>
                <div className="flex justify-between items-center py-4 border-b border-slate-100">
                  <span className="text-slate-700">Total Customers</span>
                  <span className="font-heading font-bold text-xl">{revenue?.total_customers}</span>
                </div>
                <div className="flex justify-between items-center py-4">
                  <span className="text-slate-700 font-heading font-semibold">Monthly Recurring Revenue (MRR)</span>
                  <span className="font-heading font-bold text-2xl text-violet-600">
                    ${(revenue?.active_subscriptions * 59.99).toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Message Detail Modal */}
      {selectedMessage && (
        <Dialog open={!!selectedMessage} onOpenChange={() => setSelectedMessage(null)}>
          <DialogContent className="sm:max-w-2xl">
            <DialogHeader>
              <DialogTitle className="text-2xl font-heading font-bold">{selectedMessage.subject}</DialogTitle>
            </DialogHeader>
            <div className="mt-4 space-y-4">
              <div className="bg-slate-50 rounded-xl p-4">
                <div className="text-sm text-slate-600 mb-1">From:</div>
                <div className="font-medium">{selectedMessage.user_name} ({selectedMessage.user_email})</div>
                <div className="text-sm text-slate-500 mt-2">
                  {new Date(selectedMessage.created_at).toLocaleString()}
                </div>
              </div>
              
              <div className="bg-white border border-slate-200 rounded-xl p-6">
                <p className="text-slate-700 whitespace-pre-wrap">{selectedMessage.message}</p>
              </div>

              {selectedMessage.status === 'unread' && (
                <Button
                  onClick={() => {
                    markMessageRead(selectedMessage.id);
                    setSelectedMessage(null);
                  }}
                  className="w-full bg-violet-600 hover:bg-violet-700 text-white rounded-full font-heading"
                >
                  Mark as Read
                </Button>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Important Resources Section for Admin */}
      {activeTab === 'overview' && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-8">
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-gradient-to-br from-blue-500 to-cyan-500 rounded-3xl p-8 text-white hover:shadow-2xl transition-all">
              <h3 className="text-xl font-heading font-bold mb-3">🛡️ FTC Identity Theft Resource</h3>
              <p className="text-blue-50 mb-6">
                Share this official FTC resource with clients who need to report identity theft or fraudulent accounts.
              </p>
              <a
                href="https://www.identitytheft.gov/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block bg-white text-blue-600 px-6 py-3 rounded-full font-heading font-semibold hover:bg-blue-50 transition-colors"
              >
                Report to FTC →
              </a>
            </div>

            <div className="bg-gradient-to-br from-violet-500 to-purple-500 rounded-3xl p-8 text-white hover:shadow-2xl transition-all">
              <h3 className="text-xl font-heading font-bold mb-3">📊 Credit Hero Monitoring</h3>
              <p className="text-violet-50 mb-6">
                Your affiliate link for Identity IQ. All referrals from DIY users and broker clients use your affiliate ID.
              </p>
              <a
                href="https://identityiq.com/sc-securepreferred.aspx?offercode=4312985V"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block bg-white text-violet-600 px-6 py-3 rounded-full font-heading font-semibold hover:bg-violet-50 transition-colors"
              >
                Join Credit Hero Monitoring →
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;